import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Truck } from "lucide-react";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { SendWhatsAppButton } from "@/components/SendWhatsAppButton";

const empty = {
  nombre: "", cif: "", categoria: "Impresión", persona_contacto: "",
  email: "", telefono: "", direccion: "", forma_pago: "30 días", notas: "",
};

export default function ProveedoresPage() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const load = () => api.get("/proveedores").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...empty, ...it }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form };
    Object.keys(payload).forEach((k) => payload[k] === "" && (payload[k] = null));
    if (editing) await api.patch(`/proveedores/${editing.id}`, payload);
    else await api.post("/proveedores", payload);
    setOpen(false); load();
  };

  return (
    <div className="p-8 max-w-[1800px]">
      <PageHeader
        title="Proveedores"
        subtitle="Imprentas, materias primas, montaje y logística"
        action={
          <Button onClick={openCreate} data-testid="new-proveedor-btn" className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">
            <Plus className="w-4 h-4 mr-2" /> Nuevo proveedor
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((p) => (
          <div key={p.id} data-testid={`proveedor-card-${p.id}`}
            className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-5 hover:border-amber-500/40 transition-colors">
            <div className="flex items-center gap-2 mb-1">
              <Truck className="w-4 h-4 text-amber-400" />
              <div className="text-base font-semibold truncate">{p.nombre}</div>
            </div>
            <div className="text-xs text-amber-300/80 mb-3">{p.categoria || "—"}</div>
            <div className="space-y-1 text-xs text-zinc-400">
              {p.cif && <div><span className="text-zinc-500">CIF:</span> <span className="font-mono">{p.cif}</span></div>}
              {p.persona_contacto && <div><span className="text-zinc-500">Contacto:</span> {p.persona_contacto}</div>}
              {p.telefono && (
                <div className="flex items-center gap-2">
                  <span className="text-zinc-500">Tel:</span>
                  <span>{p.telefono}</span>
                  <SendWhatsAppButton
                    to={p.telefono}
                    nombreDestinatario={p.persona_contacto || p.nombre}
                    size="xs"
                    variant="ghost"
                    iconOnly
                    testId={`send-wa-proveedor-${p.id}`}
                  />
                </div>
              )}
              {p.email && <div className="truncate"><span className="text-zinc-500">Email:</span> {p.email}</div>}
              {p.direccion && <div className="truncate"><span className="text-zinc-500">Dir.:</span> {p.direccion}</div>}
              {p.forma_pago && <div><span className="text-zinc-500">Pago:</span> {p.forma_pago}</div>}
            </div>
            <div className="flex gap-2 mt-4 pt-3 border-t border-zinc-800">
              <Button size="sm" variant="ghost" onClick={() => openEdit(p)} className="text-zinc-400 hover:text-amber-300 h-7 px-2">
                <Pencil className="w-3.5 h-3.5 mr-1" /> Editar
              </Button>
              <Button size="sm" variant="ghost"
                onClick={async () => { if (confirm(`¿Eliminar ${p.nombre}?`)) { await api.delete(`/proveedores/${p.id}`); load(); } }}
                className="text-zinc-500 hover:text-red-400 h-7 px-2"><Trash2 className="w-3.5 h-3.5" /></Button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="col-span-full text-center py-12 text-zinc-500">Sin proveedores todavía.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-2xl">
          <DialogHeader><DialogTitle>{editing ? "Editar proveedor" : "Nuevo proveedor"}</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Nombre *</Label>
                <Input required value={form.nombre} data-testid="form-proveedor-nombre"
                  onChange={(e) => setForm({ ...form, nombre: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div><Label>CIF</Label>
                <Input value={form.cif} onChange={(e) => setForm({ ...form, cif: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div><Label>Categoría</Label>
                <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {["Impresión", "Materia prima", "Montaje", "Logística", "Otro"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label>Persona de contacto</Label>
                <Input value={form.persona_contacto} onChange={(e) => setForm({ ...form, persona_contacto: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div><Label>Forma de pago</Label>
                <Select value={form.forma_pago} onValueChange={(v) => setForm({ ...form, forma_pago: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {["Contado", "Transferencia", "15 días", "30 días", "60 días"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select></div>
              <div><Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div><Label>Teléfono</Label>
                <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
              <div className="col-span-2"><Label>Dirección</Label>
                <AddressAutocomplete value={form.direccion}
                  onChange={(v) => setForm({ ...form, direccion: v })}
                  onSelect={(loc) => setForm({ ...form, direccion: loc.direccion })}
                  placeholder="Avda. de los Materiales 1, Sevilla"
                  data-testid="form-proveedor-direccion"
                  className="bg-zinc-900 border-zinc-800" /></div>
              <div className="col-span-2"><Label>Notas</Label>
                <Textarea value={form.notas} rows={2} onChange={(e) => setForm({ ...form, notas: e.target.value })} className="bg-zinc-900 border-zinc-800" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} className="bg-transparent border-zinc-700">Cancelar</Button>
              <Button type="submit" data-testid="form-proveedor-submit" className="bg-amber-500 hover:bg-amber-400 text-zinc-900 font-semibold">Guardar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
